import { Form, Modal, type UploadFile } from "antd"
import Dragger from "antd/es/upload/Dragger"
import React, { useState } from "react"

import draggerFunctions from "../../../helpers/draggerFunctions"
import { onRequiredTypeChange } from "../../../helpers/format"
import { type ApplicationState } from "../../../store"
import DraggerInside from "../../DraggerInside"
import KintoButton, { KintoButtonThemes } from "../../KintoButton"

const { confirm } = Modal

export interface ImagesFormData {
  licensePlateFileList: UploadFile[]
  odometerFileList: UploadFile[]
  carFrontFileList: UploadFile[]
  carRightFileList: UploadFile[]
  carBackFileList: UploadFile[]
  carLeftFileList: UploadFile[]
  terms: boolean
}

type OwnProps = {
  afterFinish?: (formData: ImagesFormData) => void
  handleReturn?: () => void
  defaults?: ApplicationState["onefleetdevolucao"]
}

const ImagesForm = (props: OwnProps): React.ReactElement => {
  const [formData] = Form.useForm()
  const [licensePlateFileList, setLicensePlateFileList] = useState<UploadFile[]>(props.defaults?.data.images?.licensePlateFileList ?? [])
  const [odometerFileList, setOdometerFileList] = useState<UploadFile[]>(props.defaults?.data.images?.odometerFileList ?? [])
  const [carFrontFileList, setCarFrontFileList] = useState<UploadFile[]>(props.defaults?.data.images?.carFrontFileList ?? [])
  const [carRightFileList, setCarRightFileList] = useState<UploadFile[]>(props.defaults?.data.images?.carRightFileList ?? [])
  const [carBackFileList, setCarBackFileList] = useState<UploadFile[]>(props.defaults?.data.images?.carBackFileList ?? [])
  const [carLeftFileList, setCarLeftFileList] = useState<UploadFile[]>(props.defaults?.data.images?.carLeftFileList ?? [])

  function handleFormFinish () {
    showConfirm()
  }

  const showConfirm = () => {
    confirm({
      title: "TERMO DE DEVOLUÇÃO",
      content: (
        <div>
          DECLARO ter ciência que, após a finalização deste checklist, o contrato de Locação de Veículos, referente ao veículo objeto do checklist, será encerrado em até 7 dias. E, após o encerramento, o time financeiro da KINTO enviará, em até 20 dias, a cobrança pro rata da mensalidade, e, posteriormente, caso ocorram, de  eventuais cobranças adicionais, como de avarias, KM excedente etc.<br/>Lembrando que os órgãos de trânsito têm o prazo de até 5 anos para emitir a penalidade de uma possível infração, e esta será devidamente faturada e cobrada da Locatária.
        </div>
      ),
      async onOk () {
        if (props.afterFinish) {
          props.afterFinish({
            licensePlateFileList,
            odometerFileList,
            carFrontFileList,
            carRightFileList,
            carBackFileList,
            carLeftFileList,
            terms: true
          })
        }
      },
      cancelText: "Cancelar",
      centered: true,
      okCancel: false
    })
  }

  return (
    <Form
      onFinish={handleFormFinish}
      form={formData}
      layout="vertical"
      initialValues={props.defaults?.data.images}
      requiredMark={onRequiredTypeChange}
    >
      <Form.ErrorList />

      <Form.Item
        name="licensePlateImages"
        label="Foto da placa"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.licensePlateFileList?.[0], fileList: props.defaults?.data.images?.licensePlateFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 2, setLicensePlateFileList) }}
          fileList={licensePlateFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item
        name="odometerImages"
        label="Foto do odômetro"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.odometerFileList?.[0], fileList: props.defaults?.data.images?.odometerFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setOdometerFileList) }}
          fileList={odometerFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item
        name="carFrontImages"
        label="Foto frontal"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.carFrontFileList?.[0], fileList: props.defaults?.data.images?.carFrontFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setCarFrontFileList) }}
          fileList={carFrontFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item
        name="carLeftImages"
        label="Foto da lateral esquerda"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.carLeftFileList?.[0], fileList: props.defaults?.data.images?.carLeftFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setCarLeftFileList) }}
          fileList={carLeftFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item
        name="carRightImages"
        label="Foto da lateral direita"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.carRightFileList?.[0], fileList: props.defaults?.data.images?.carRightFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setCarRightFileList) }}
          fileList={carRightFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item
        name="carBackImages"
        label="Foto da traseira"
        required={true}
        rules={[{ validator: async (_, value) => { if (!value?.fileList?.length) throw new Error("Imagem não inserida!") } }]}
        initialValue={{ file: props.defaults?.data.images?.carBackFileList?.[0], fileList: props.defaults?.data.images?.carBackFileList }}
      >
        <Dragger
          accept=".png,.jpg,.jpeg"
          onChange={(info) => { draggerFunctions.handleImageChange(info, 1, setCarBackFileList) }}
          fileList={carBackFileList}
          customRequest={draggerFunctions.uploadFile}
          beforeUpload={draggerFunctions.beforeUploadFile}
        ><DraggerInside /></Dragger>
      </Form.Item>

      <Form.Item label="">
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <KintoButton theme={KintoButtonThemes.outlined} onClick={props.handleReturn}>Voltar</KintoButton>
          <KintoButton theme={KintoButtonThemes.contained} htmlType="submit">Próximo</KintoButton>
        </div>
      </Form.Item>
    </Form>
  )
}

export default ImagesForm
